import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { VacationPeriod, NotificationQueue } from '../types';

// State-Definitionen
export interface AdminState {
  tab: string;
  showAddLocationModal: boolean;
  isAdmin: boolean;
  showAdminLogin: boolean;
  vacationPeriods: VacationPeriod[];
  notifications: NotificationQueue[];
}

// Initialer Zustand — Tab aus sessionStorage wiederherstellen
const VALID_TABS = ['dashboard','calendar','bookings','customers','services','locations','employees','resources','notifications','planning','settings'];
const savedTab = sessionStorage.getItem('adminTab');
const initialState: AdminState = {
  tab: (savedTab && VALID_TABS.includes(savedTab)) ? savedTab : 'dashboard',
  showAddLocationModal: false,
  isAdmin: false,
  showAdminLogin: false,
  vacationPeriods: [],
  notifications: []
};

// Action-Typen
export type AdminAction =
  | { type: 'SET_TAB'; payload: string }
  | { type: 'TOGGLE_ADD_LOCATION_MODAL'; payload?: boolean }
  | { type: 'SET_ADMIN'; payload: boolean }
  | { type: 'SET_SHOW_ADMIN_LOGIN'; payload: boolean }
  | { type: 'SET_VACATION_PERIODS'; payload: VacationPeriod[] }
  | { type: 'SET_NOTIFICATIONS'; payload: NotificationQueue[] };

// Reducer-Funktion
const adminReducer = (state: AdminState, action: AdminAction): AdminState => {
  switch (action.type) {
    case 'SET_TAB':
      return { ...state, tab: action.payload };
    case 'TOGGLE_ADD_LOCATION_MODAL':
      return { ...state, showAddLocationModal: action.payload ?? !state.showAddLocationModal };
    case 'SET_ADMIN':
      return { ...state, isAdmin: action.payload };
    case 'SET_SHOW_ADMIN_LOGIN':
      return { ...state, showAdminLogin: action.payload };
    case 'SET_VACATION_PERIODS':
      return { ...state, vacationPeriods: action.payload };
    case 'SET_NOTIFICATIONS':
      return { ...state, notifications: action.payload };
    default:
      return state;
  }
};

// Context erstellen
const AdminStateContext = createContext<AdminState | undefined>(undefined);
const AdminDispatchContext = createContext<React.Dispatch<AdminAction> | undefined>(undefined);

// Provider-Komponente
export const AdminProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(adminReducer, initialState);

  return (
    <AdminStateContext.Provider value={state}>
      <AdminDispatchContext.Provider value={dispatch}>
        {children}
      </AdminDispatchContext.Provider>
    </AdminStateContext.Provider>
  );
};

// Custom Hooks
export const useAdminState = () => {
  const context = useContext(AdminStateContext);
  if (context === undefined) {
    throw new Error('useAdminState muss innerhalb von AdminProvider verwendet werden');
  }
  return context;
};

export const useAdminDispatch = () => {
  const context = useContext(AdminDispatchContext);
  if (context === undefined) {
    throw new Error('useAdminDispatch muss innerhalb von AdminProvider verwendet werden');
  }
  return context;
}; 